import { useCallback, useState, useEffect, createContext, useContext } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'

import styled from 'styled-components'
import Tooltip from 'components/Tooltip'
import { color } from 'styles/colors'

import { useSessionContext } from 'hooks/SessionContext'
import LockIcon from '@mui/icons-material/LockOutlined'
import UnlockIcon from '@mui/icons-material/LockOpenOutlined'

import Api from 'services/api'

const PremiumFeaturesContext = createContext()

const propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.shape()
  ]).isRequired
}

export const PremiumFeaturesContextProvider = ({ children }) => {
  const { session, isSessionLoaded } = useSessionContext()
  const location = useLocation()
  const navigate = useNavigate()

  const [activePremiumFeatures, setActivePremiumFeatures] = useState([])
  const [publishedPremiumFeatures, setPublishedPremiumFeatures] = useState([])
  const [tier, setTier] = useState(null)
  const [arePremiumFeaturesLoaded, setPremiumFeaturesLoaded] = useState(false)
  const [trialEndDates] = useState([])

  useEffect(() => {
    if (!isSessionLoaded) {
      return
    }
    Api.Paywall.premiumFeatures(session.dealerId)
      .then((res) => {
        setActivePremiumFeatures(res.featureCodes)
        setPublishedPremiumFeatures(res.publishedFeatures)
        setTier(res.tier)
        setPremiumFeaturesLoaded(true)

        res.publishedFeatures.forEach((featureCode) => {
          Api.Paywall.getTrialEndDate(featureCode)
            .then((res) => {
              if (res.trialEndDate) {
                trialEndDates.push({ featureCode: featureCode, endDate: res.trialEndDate })
              }
            })
        })
      })
      .catch(() => {
        setActivePremiumFeatures([])
        setPublishedPremiumFeatures([])
        setTier(null)
        setPremiumFeaturesLoaded(true)
      })
  }, [location.pathname, location.search, session, navigate])

  const hasAccess = useCallback((featureCode) => {
    if (arePremiumFeaturesLoaded === false || activePremiumFeatures.length === 0) {
      return false
    }

    if (activePremiumFeatures.includes(featureCode)) {
      return true
    } else {
      return false
    }
  }, [arePremiumFeaturesLoaded, activePremiumFeatures]);

  const showUpgrade = useCallback((featureCode) => {
    if (session.isFsr || session.isAdmin || !arePremiumFeaturesLoaded || tier !== 'basic' || inFreeTrial(featureCode)) {
      return false
    }

    if(tier === 'basic' && publishedPremiumFeatures.includes(featureCode)) {
      return true
    } else {
      return false
    }
  }, [arePremiumFeaturesLoaded, publishedPremiumFeatures, tier]);

  const inFreeTrial = useCallback((featureCode) => {
    if (session.isFsr || session.isAdmin || !arePremiumFeaturesLoaded || tier !== 'basic') {
      return false
    }

    if (publishedPremiumFeatures.includes(featureCode) && activePremiumFeatures.includes(featureCode)) {
      return true
    } else {
      return false
    }
  }, [arePremiumFeaturesLoaded, publishedPremiumFeatures, activePremiumFeatures]);

  const published = useCallback((featureCode) => {
    if (publishedPremiumFeatures.includes(featureCode) && arePremiumFeaturesLoaded) {
      return true
    } else {
      return false
    }
  }, [arePremiumFeaturesLoaded, publishedPremiumFeatures]);

  const hoverTextMessage = useCallback((featureCode, defaultMessage) => {
    if (session.isFsr || session.isAdmin || !arePremiumFeaturesLoaded) {
      return ''
    } else if (showUpgrade(featureCode)) {
      return `Upgrade to Diagnostics Premium to gain access to this feature.`
    } else if (inFreeTrial(featureCode)) {
      const trial = trialEndDates.find((trial) => trial.featureCode === featureCode);

      if (trial?.endDate) {
        return `Available during Diagnostics Premium Free-Trial until ${new Date(trial.endDate).toDateString()}. Upgrade now to keep this feature.`
      } else {
        return 'Available during Diagnostics Premium Free-Trial. Upgrade now to keep this feature.'
      }
    } else {
      return defaultMessage
    }
  }, [inFreeTrial, showUpgrade]);

  const LockIcon = ({featureCode, defaultHover = '', featureComponentType='card'}) => {
    if (session.isFsr || session.isAdmin || !arePremiumFeaturesLoaded) {
      return <></>
    }
    if (published && showUpgrade(featureCode)) {
      return <Tooltip placement="top" title={hoverTextMessage(featureCode, defaultHover)}>
        <S.BackgroundCircle feature_component_type={featureComponentType}>
          <S.LockIcon />
        </S.BackgroundCircle>
      </Tooltip>
    } else if (published){
      return <Tooltip placement="top" title={hoverTextMessage(featureCode, defaultHover)}>
        <S.BackgroundCircle feature_component_type={featureComponentType}>
          <S.UnlockIcon />
        </S.BackgroundCircle>
      </Tooltip>
    } else {
      return <></>
    }
  }

  return (
    <PremiumFeaturesContext.Provider value={{ hasAccess, showUpgrade, inFreeTrial, hoverTextMessage, LockIcon, tier, published, arePremiumFeaturesLoaded }}>
      {children}
    </PremiumFeaturesContext.Provider>
  )
}

PremiumFeaturesContextProvider.propTypes = propTypes

export const usePremiumFeaturesContext = () => useContext(PremiumFeaturesContext)

const iconStyle = `
  font-size: 18px;
`
const backgroundStyleCorner = `
  width: 25px;
  height: 25px;
  border-radius: 50%;
  background-color: ${color('border')};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`

const backgroundStyleInline = `
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`

const S = {
  LockIcon: styled(LockIcon)`
    color: ${color('premiumLockedIcon')};
    ${iconStyle}
 `,
  UnlockIcon: styled(UnlockIcon)`
    color: ${color('premiumUnlockedIcon')};
    ${iconStyle}
 `,
  BackgroundCircle: styled.div<{ feature_component_type: string | null }>`
    position: ${({ feature_component_type }) => feature_component_type !== 'title' ? 'absolute' : 'relative'};
    top: ${({ feature_component_type }) => feature_component_type === 'card' ? '-5px' : feature_component_type === 'title' ? '0px' : '-10px'};
    right: ${({ feature_component_type }) => feature_component_type === 'card' ? '-5px' : '-10px'};
    ${({ feature_component_type }) => feature_component_type !== 'title' ? backgroundStyleCorner : backgroundStyleInline};
  `
}
